import { useCallback, useEffect, useRef, useState } from 'react'
import { flushSync } from 'react-dom'
import { Button, Card, GrovusProvider, Text } from '@grovus/design-system'
import App from '../App'
import { AdminScreen } from '../admin/AdminScreen'
import { fetchAdminUsers } from '../admin/api'
import { isAdminPath, sectionOf, sectionPath } from '../admin/sections'
import { fetchCompanies, toClient } from '../data/companies'
import { joinClients, resolveClient, type Client } from '../data/clients'
import { AUTH_COPY, DICT, type Dict, type Lang } from '../i18n'
import { loadActiveClient, loadLang, loadTheme, saveActiveClient, saveLang, saveTheme, type Theme } from '../lib/preferences'
import { InviteLinkPage } from '../members/InviteLinkPage'
import { captureInviteToken, clearInviteToken, heldInviteToken, INVITE_PATH } from '../members/inviteToken'
import { NoAccessPage } from '../members/NoAccessPage'
import { useInvitations } from '../members/useInvitations'
import { FirstRun } from '../onboarding/FirstRun'
import { RequestAccessScreen, type RequestExit } from '../requests/RequestAccessScreen'
import { leaveGuestView } from '../data/shareLinks'
import { clearGuestMarker, readGuestMarker, writeGuestMarker } from '../share/guestMarker'
import { GuestEndedPage, ShareLinkPage } from '../share/ShareLinkPage'
import { SHARE_PATH } from '../share/shareToken'
import { AuthLayout } from './AuthLayout'
import { SignInScreen } from './SignInScreen'
import { fetchSession, signOut, type GuestSession, type Membership } from './api'
import { afterFailure, afterUnauthenticated, decideEntry, transition, type BoundaryState, type Intent, type InviteTerminal } from './entry'
import { createReconciler, type Read } from './reconcile'
import { REFRESH_MS } from './refreshQueue'
import { authGeneration, reportGuestChanged, reportSignedOut, resetAuthWork, subscribeAuth, subscribeGuestChanged } from './session'
import { takeSignInReturn } from './signInReturn'

const REQUEST_PATH = '/request-access'

type State = BoundaryState

/** `GET /api/companies` joined with the session's memberships; `null` on a failed or stale read. */
async function readClients(memberships: Membership[], generation: number, signal?: AbortSignal): Promise<Client[] | null> {
  const result = await fetchCompanies(undefined, signal)
  if (result.status !== 'ok' || generation !== authGeneration()) return null
  return joinClients(result.companies, memberships, toClient)
}

/** One read of the session, then the companies, for `reconcile()`. */
async function readInventory(signal: AbortSignal): Promise<Read | null> {
  const generation = authGeneration()
  const session = await fetchSession()
  if (generation !== authGeneration()) return null
  if (session.status === 'unauthenticated') return { kind: 'unauthenticated' }
  if (session.status !== 'authenticated') return null
  const clients = await readClients(session.memberships, generation, signal)
  if (clients === null) return null
  return { kind: 'ok', inventory: { user: session.user, memberships: session.memberships, clients } }
}

/**
 * Gives the panel its history on entry (GRO-188, B10): an in-app `/` below the first
 * section, so Back from it stays in the app instead of replaying the sign-in callback. An
 * entry that already carries the marker was set up earlier in this tab (a reload, a
 * restoration, a Forward into the panel) and keeps its history.
 */
function enterAdminHistory() {
  const section = sectionOf(window.location.pathname) ?? 'users'
  if (!(window.history.state as { grovusAdmin?: boolean } | null)?.grovusAdmin) {
    window.history.replaceState(null, '', '/')
    window.history.pushState({ grovusAdmin: true }, '', sectionPath(section))
  } else if (sectionOf(window.location.pathname) === null) {
    window.history.replaceState({ grovusAdmin: true }, '', sectionPath(section))
  }
}

/** The one company a guest grant shows, in the shape `App` reads. The role is never shown. */
function guestClient(guest: GuestSession): Client {
  return {
    id: guest.companyId,
    name: guest.companyName,
    accounts: guest.accounts,
    isDemo: guest.accounts.every((a) => a.isDemo),
    role: 'viewer',
  }
}

function CheckingScreen({ t }: { t: Dict }) {
  return (
    <AuthLayout>
      <Card>
        <Text fz="sm" role="status">{t.authChecking}</Text>
      </Card>
    </AuthLayout>
  )
}

function UnavailableScreen({ message, retryLabel, onRetry }: { message: string; retryLabel: string; onRetry: () => void }) {
  return (
    <AuthLayout>
      <Card>
        <Text fz="sm" role="alert">{message}</Text>
        <Button mt="md" onClick={onRetry}>{retryLabel}</Button>
      </Card>
    </AuthLayout>
  )
}

/**
 * Sits above App. Checking calls GET /api/auth/session, then, once that resolves
 * authenticated, GET /api/companies, and joins the two (`decideEntry`): 401 (either
 * request) renders SignInScreen, a network failure or non-2xx renders retry (never the
 * app), no membership renders No client access, and a non-empty joined list mounts the
 * app. After entry every change of the session or the companies goes through one
 * coordinator, `reconcile(intent?)`, which applies a read and the intents sharing it in
 * one transition (GRO-106 plan, Tasks 5-7a).
 * /sign-in and any direct/reloaded app URL go through this same boundary.
 * `/request-access` without a session renders RequestAccessScreen instead of SignInScreen. A 401 from any
 * other request (session.ts) and a confirmed sign-out both land here through the same
 * subscribeAuth path, so App never has to know about SignInScreen or vice versa.
 */
export function AuthBoundary() {
  const [state, setState] = useState<State>({ status: 'checking' })
  const [lang, setLangState] = useState<Lang>(loadLang)
  const [theme, setThemeState] = useState<Theme>(loadTheme)
  const [attempt, setAttempt] = useState(0)
  const t = DICT[lang]

  const [storedClientId, setStoredClientId] = useState<string | null>(null)
  const storedRef = useRef(storedClientId)
  // The wizard's entry into the company it created: pending while `reconcile()` reads,
  // failed when that read failed, with the company id Retry reads for.
  const [completion, setCompletion] = useState<'pending' | 'failed' | null>(null)
  const createdRef = useRef<string | null>(null)

  // Written eagerly with every transition, so a commit applied a microtask after another
  // one decides from the state that one left, not from the last render.
  const stateRef = useRef(state)
  const setBoundary = useCallback((next: State) => {
    stateRef.current = next
    setState(next)
  }, [])
  const setStored = useCallback((userId: string, id: string) => {
    storedRef.current = id
    setStoredClientId(id)
    saveActiveClient(userId, id)
  }, [])

  // Set when `leaveAdmin()` acts, cleared when a `run()` enters the panel, so a panel is
  // left once however many of its requests are denied. Not tied to the generation that
  // entered the panel: a failed sign-out moves the generation on and leaves the panel up,
  // and a later denial must still clear it (GRO-115 plan, R4-F1).
  const leavingRef = useRef(false)

  // The attempt whose admin entry is in flight, from the start of its `run()` on an admin
  // path until its admin read answers: a Back out of `/admin` in that window retires it.
  const adminEntryRef = useRef<object | null>(null)

  // `unavailable` came from a failed admin read: a Back to a main-app path runs entry for it.
  const adminFailedRef = useRef(false)

  // The session's received invitations, one list for every reader (plan, Task 20).
  const invitations = useInvitations()
  const { refetch: refetchInvitations, reset: resetInvitations } = invitations

  // The account panel's reconcile (GRO-41), the add-client flow (GRO-82), the focus
  // re-check and every GRO-106 caller read through here. Reads run one at a time behind a
  // deadline, so an older answer never replaces a newer inventory (GRO-82 design, D5).
  const [reconcile] = useState(() => createReconciler({
    read: readInventory,
    commit: (inventory, intents) => {
      const current = stateRef.current
      const activeId = current.status === 'authenticated' ? resolveClient(current.clients, storedRef.current).id : storedRef.current
      const next = transition(current, inventory, intents, activeId)
      setBoundary(next.state)
      if (next.active !== null) setStored(inventory.user.id, next.active)
      return next.outcomes
    },
    signedOut: () => setBoundary(afterUnauthenticated(stateRef.current)),
    failed: (mismatch) => setBoundary(afterFailure(stateRef.current, mismatch)),
    deadlineMs: REFRESH_MS,
  }))
  const refreshClients = useCallback(async (): Promise<Client[] | null> => {
    const result = await reconcile()
    return result.status === 'ok' && result.inventory.clients.length > 0 ? result.inventory.clients : null
  }, [reconcile])

  useEffect(() => {
    // The generation this probe was issued under. A sign-out or an expiry reported while it
    // is in flight moves the generation on, and its answer is then stale by definition: a
    // resolved result that was true before the session ended must not put the app back on
    // screen.
    const generation = authGeneration()
    let current = true
    setBoundary({ status: 'checking' })
    const path = () => window.location.pathname
    const entry = {}
    adminEntryRef.current = isAdminPath(path()) ? entry : null
    adminFailedRef.current = false
    const entryDone = () => {
      if (adminEntryRef.current === entry) adminEntryRef.current = null
    }

    async function run() {
      // `/share#<token>` opens its link before any session decision (GRO-83).
      if (path() === SHARE_PATH) {
        setBoundary({ status: 'share' })
        return
      }
      // `/invite#<token>`: the token moves to sessionStorage and the fragment leaves the URL
      // before anything else runs (design, "Invite link page").
      captureInviteToken()
      const tokenHeld = () => heldInviteToken() !== null
      const sessionResult = await fetchSession()
      if (!current || generation !== authGeneration()) return
      if (sessionResult.status !== 'authenticated') {
        const decision = decideEntry({
          session: sessionResult.status, tokenHeld: tokenHeld(), path: path(), guestMarker: readGuestMarker() !== null,
        })
        if (decision === 'invite') {
          setBoundary({ status: 'invite', user: null, memberships: [], clients: [], terminal: null })
        } else if (decision === 'guest' && sessionResult.status === 'guest') {
          writeGuestMarker(sessionResult.guest)
          setBoundary({ status: 'guest', guest: sessionResult.guest, unavailable: false, reloadKey: authGeneration() })
        } else if (decision === 'guestEnded') {
          setBoundary({ status: 'guestEnded' })
        } else {
          // The request page belongs to a visitor without a session: the callback sent them
          // here with a request ticket. With a session the path is ignored like any other.
          if (decision === 'unauthenticated') setBoundary(path() === REQUEST_PATH ? { status: 'request' } : { status: 'unauthenticated' })
          else setBoundary({ status: 'unavailable' })
        }
        return
      }
      let { user, memberships } = sessionResult
      // A member's view: whatever guest grant this browser held is no longer its story.
      clearGuestMarker()
      void refetchInvitations()

      // A signed-in `/sign-in` is a callback error left in history: the app opens on `/`.
      if (path() === '/sign-in') window.history.replaceState(null, '', '/')

      // A sign-in started at `/admin` or `/invite` comes back to `/` (GRO-115 plan, R4-F2).
      // The stored destination only picks the branch below; the API still decides.
      const destination = takeSignInReturn()
      if (destination !== null && window.location.pathname === '/') {
        window.history.replaceState(null, '', destination)
      }

      // A held token: the invite page, whatever the user's companies (design, "Boundary flow").
      if (decideEntry({ session: 'authenticated', tokenHeld: tokenHeld(), path: path() }) === 'invite') {
        if (window.location.pathname !== INVITE_PATH) window.history.replaceState(null, '', INVITE_PATH)
        const clients = await readClients(memberships, generation)
        if (!current || generation !== authGeneration()) return
        setBoundary(clients === null ? { status: 'unavailable' } : { status: 'invite', user, memberships, clients, terminal: null })
        return
      }

      // Before companies, onboarding and the automatic creation: a platform admin who
      // belongs to no company reaches the panel and is never sent to onboarding. A denial
      // continues to the ordinary flow in this same run, so entry cannot restart itself.
      if (decideEntry({ session: 'authenticated', tokenHeld: false, path: path() }) === 'admin') {
        const admin = await fetchAdminUsers()
        if (!current || generation !== authGeneration()) return
        entryDone()
        // A Back that left `/admin` while the read was out: the ordinary flow, whatever it answered.
        if (isAdminPath(path())) {
          if (admin.status === 'ok') {
            enterAdminHistory()
            leavingRef.current = false
            setBoundary({ status: 'admin', user, users: admin.body })
            return
          }
          if (admin.status !== 'denied') {
            if (admin.status !== 'expired') {
              adminFailedRef.current = true
              setBoundary({ status: 'unavailable' })
            }
            return
          }
        }
      }

      // Strictly after the session resolves authenticated, not in parallel with it: an
      // unauthenticated visitor must never send this request, and the boundary stays
      // `checking` until both have answered so an authenticated user with existing
      // companies never sees No client access flash before App mounts.
      let clients = await readClients(memberships, generation)
      if (!current || generation !== authGeneration()) return
      let decision = decideEntry({ session: 'authenticated', tokenHeld: false, path: '/', memberships, clients })

      // Memberships that join to nothing: the two reads straddled a membership change, so
      // both are read once more before the boundary believes either (plan, 5.2).
      if (decision === 'mismatch') {
        const again = await fetchSession()
        if (!current || generation !== authGeneration()) return
        if (again.status === 'unauthenticated') {
          setBoundary({ status: 'unauthenticated' })
          return
        }
        if (again.status !== 'authenticated') {
          setBoundary({ status: 'unavailable' })
          return
        }
        ({ user, memberships } = again)
        clients = await readClients(memberships, generation)
        if (!current || generation !== authGeneration()) return
        decision = decideEntry({ session: 'authenticated', tokenHeld: false, path: '/', memberships, clients })
      }

      if (decision === 'authenticated' && clients) {
        const stored = loadActiveClient(user.id)
        storedRef.current = stored
        setStoredClientId(stored)
        setBoundary({ status: 'authenticated', user, memberships, clients, notice: null })
      } else if (decision === 'noAccess') {
        setBoundary({ status: 'noAccess', user, memberships, removed: null })
      } else {
        setBoundary({ status: 'unavailable' })
      }
    }

    void run().finally(entryDone)
    return () => {
      current = false
      entryDone()
    }
  }, [attempt])

  useEffect(() => (
    subscribeAuth((reason) => {
      setCompletion(null)
      resetInvitations()
      // While a token is held, or a terminal invite page shows, the page stays, signed out.
      const current = stateRef.current
      // A guest's `401`: the link was revoked or expired. The ended-guest page, no reload.
      if (current.status === 'guest' || current.status === 'guestEnded') {
        setBoundary({ status: 'guestEnded' })
        return
      }
      if (heldInviteToken() !== null || (current.status === 'invite' && current.terminal !== null)) {
        setBoundary(current.status === 'invite'
          ? afterUnauthenticated(current)
          : { status: 'invite', user: null, memberships: [], clients: [], terminal: null })
        return
      }
      setBoundary({ status: 'unauthenticated', expired: reason === 'expired' })
    })
  ), [resetInvitations, setBoundary])

  /**
   * Leaves the admin panel after a `denied`, or after a Back out of `/admin`: aborts every
   * admin request still in flight, which makes `send` drop the answers already on their
   * way, then runs entry again under the new generation on the current path. Acts only in
   * the `admin` state and once per panel.
   */
  const leaveAdmin = useCallback(() => {
    if (stateRef.current.status !== 'admin' || leavingRef.current) return
    leavingRef.current = true
    resetAuthWork()
    setAttempt((n) => n + 1)
  }, [])

  /**
   * The guest view's only reconciliation (GRO-83 design, "One browser, one guest grant"):
   * abort the reporting requests, then read the session once. The same company keeps the
   * view and moves `reloadKey`, so the screens fetch again with the selections `App`
   * holds; another company replaces the view; a `401` ends it; a member enters as one; a
   * failed read keeps everything and offers Retry. The list's `404` asks with `abort:
   * false`: nothing else is aborted, so the same company keeps `reloadKey` and the list
   * keeps its notice and Retry, and no second re-read runs until the guest retries.
   */
  const recheckGuest = useCallback(async (abort = true) => {
    if (stateRef.current.status !== 'guest') return
    if (abort) resetAuthWork()
    const generation = authGeneration()
    const result = await fetchSession()
    const current = stateRef.current
    if (generation !== authGeneration() || current.status !== 'guest') return
    if (result.status === 'guest') {
      writeGuestMarker(result.guest)
      const same = result.guest.companyId === current.guest.companyId
      setBoundary({
        status: 'guest', guest: result.guest, unavailable: false, reloadKey: abort || !same ? generation : current.reloadKey,
      })
    } else if (result.status === 'unauthenticated') {
      setBoundary({ status: 'guestEnded' })
    } else if (result.status === 'authenticated') {
      setAttempt((n) => n + 1)
    } else {
      setBoundary({ ...current, unavailable: true })
    }
  }, [setBoundary])

  // History moves into the panel or out of an admin entry still in flight. Inside the panel
  // `AdminScreen` owns `popstate`; main-app paths have no history handling.
  useEffect(() => {
    function onPopState() {
      const status = stateRef.current.status
      const admin = isAdminPath(window.location.pathname)
      if (status !== 'admin' && admin) {
        setAttempt((n) => n + 1)
      } else if (status === 'checking' && adminEntryRef.current !== null && !admin) {
        adminEntryRef.current = null
        resetAuthWork()
        setAttempt((n) => n + 1)
      } else if (status === 'unavailable' && adminFailedRef.current && !admin) {
        setAttempt((n) => n + 1)
      }
    }
    window.addEventListener('popstate', onPopState)
    return () => window.removeEventListener('popstate', onPopState)
  }, [])

  useEffect(() => subscribeGuestChanged(() => { void recheckGuest() }), [recheckGuest])

  /**
   * "Leave guest view": the cookie, the marker and the other tabs, then the sign-in screen.
   * Only a confirmed leave goes on; otherwise the cookie still grants the view, so it stays.
   */
  const leaveGuest = useCallback(async () => {
    if (!await leaveGuestView()) return false
    resetAuthWork()
    clearGuestMarker()
    reportGuestChanged()
    window.history.replaceState(null, '', '/')
    setBoundary({ status: 'unauthenticated' })
    return true
  }, [setBoundary])

  const enterAgain = useCallback(() => {
    window.history.replaceState(null, '', '/')
    setAttempt((n) => n + 1)
  }, [])

  useEffect(() => {
    function recheck() {
      const status = stateRef.current.status
      if (status === 'guest') {
        void recheckGuest()
        return
      }
      if (status === 'authenticated' || status === 'wizard' || status === 'noAccess' || status === 'invite') {
        void reconcile()
        if (status !== 'invite' || stateRef.current.status === 'invite' && stateRef.current.user !== null) void refetchInvitations()
        return
      }
      if (status !== 'admin') return
      // The admin panel keeps its own probe and never reads companies (plan, 6.6). The
      // generation is the one current at this recheck, never the one that entered the panel.
      const generation = authGeneration()
      fetchSession().then(async (result) => {
        if (generation !== authGeneration()) return
        if (result.status !== 'authenticated') {
          setBoundary(result.status === 'unauthenticated' ? { status: 'unauthenticated' } : { status: 'unavailable' })
          return
        }
        const admin = await fetchAdminUsers()
        if (admin.status === 'denied') {
          leaveAdmin()
          return
        }
        const current = stateRef.current
        if (admin.status === 'ok' && current.status === 'admin') {
          setBoundary({ status: 'admin', user: current.user, users: admin.body })
        }
      })
    }
    window.addEventListener('focus', recheck)
    return () => window.removeEventListener('focus', recheck)
  }, [reconcile, leaveAdmin, setBoundary, refetchInvitations, recheckGuest])

  // Back/forward restoration. Hiding the page drops the app rather than freezing it with
  // its rows: whatever the browser stores has no data in it, and the pending requests that
  // would have refilled it are aborted. The restored page is checking, not authenticated,
  // so a fresh App mounts only after the session revalidates.
  useEffect(() => {
    let hidden = false
    function onPageHide() {
      hidden = true
      resetAuthWork()
      // flushSync, not a plain setState: a state update from a native listener is batched
      // into a later task, and the browser may freeze the page for its back/forward cache
      // as soon as this handler returns. Whatever is in the DOM at that moment is what
      // gets stored, so the app has to be gone before the handler returns, not after.
      flushSync(() => setBoundary({ status: 'checking' }))
    }
    function onPageShow(event: Event & { persisted?: boolean }) {
      if (!hidden && !event.persisted) return
      hidden = false
      setAttempt((n) => n + 1)
    }
    window.addEventListener('pagehide', onPageHide)
    window.addEventListener('pageshow', onPageShow)
    return () => {
      window.removeEventListener('pagehide', onPageHide)
      window.removeEventListener('pageshow', onPageShow)
    }
  }, [setBoundary])

  // Runs for the initial value too, not only changes: a saved `ru` must own the
  // attribute before anything else reads it, and a plain useState initializer cannot
  // reach outside React to set it.
  useEffect(() => {
    document.documentElement.lang = lang
  }, [lang])

  const setLang = useCallback((next: Lang) => {
    setLangState(next)
    saveLang(next)
  }, [])

  const toggleTheme = useCallback(() => {
    setThemeState((prev) => {
      const next: Theme = prev === 'light' ? 'dark' : 'light'
      saveTheme(next)
      return next
    })
  }, [])

  /**
   * Every sign-out ends the invite flow except "Use another account" on the invite page,
   * which passes `keepInvite` so the visitor comes back to the same invitation.
   */
  const handleSignOut = useCallback(async (keepInvite = false): Promise<'ok' | 'error'> => {
    if (!keepInvite) clearInviteToken()
    resetAuthWork()
    const result = await signOut()
    if (result.status === 'ok') {
      reportSignedOut()
      return 'ok'
    }
    return 'error'
  }, [])

  const leaveRequest = useCallback((reason: RequestExit) => {
    if (reason === 'admitted') window.history.replaceState(null, '', '/')
    setBoundary({ status: 'unauthenticated' })
  }, [setBoundary])

  const signOutAll = useCallback(() => handleSignOut(), [handleSignOut])

  const handleSelectClient = useCallback((id: string) => {
    if (stateRef.current.status === 'authenticated') setStored(stateRef.current.user.id, id)
  }, [setStored])

  /** A terminal invite page rendered: the token goes, the page stays (design, token lifetime). */
  const inviteTerminal = useCallback((kind: InviteTerminal) => {
    clearInviteToken()
    const current = stateRef.current
    if (current.status === 'invite') setBoundary({ ...current, terminal: kind })
  }, [setBoundary])

  /** Continue or Open <client>: the invite flow is over, and entry decides again. */
  const leaveInvite = useCallback((companyId?: string) => {
    clearInviteToken()
    const current = stateRef.current
    if (companyId !== undefined && current.status === 'invite' && current.user !== null) saveActiveClient(current.user.id, companyId)
    window.history.replaceState(null, '', '/')
    setAttempt((n) => n + 1)
  }, [])

  const dismissNotice = useCallback(() => {
    const current = stateRef.current
    if (current.status === 'authenticated') setBoundary({ ...current, notice: null })
  }, [setBoundary])

  /**
   * First run's created company enters the shell through the transition owner (plan,
   * Task 7a): its role comes from the session, never from the create answer. A company
   * still missing after one more read, or a failed read, keeps the wizard with Retry,
   * which repeats only the read.
   */
  const completeFirstRun = useCallback(async () => {
    const companyId = createdRef.current
    if (companyId === null) return
    setCompletion('pending')
    const intent: Intent = { kind: 'created', companyId }
    let result = await reconcile(intent)
    if (result.status === 'ok' && result.outcome === 'missing') result = await reconcile(intent)
    if (result.status === 'unauthenticated') return
    if (result.status === 'ok' && result.outcome === 'applied') {
      createdRef.current = null
      setCompletion(null)
      return
    }
    setCompletion('failed')
  }, [reconcile])

  // One provider element for every branch, at the same position on every render: the
  // palette follows `theme` as a prop, and a transition between states (checking to
  // authenticated, authenticated to sign-in) never remounts it or the DOM beneath it
  // for that reason alone.
  return (
    <GrovusProvider colorScheme={theme}>
      {state.status === 'checking' && <CheckingScreen t={t} />}
      {state.status === 'unavailable' && (
        <UnavailableScreen
          message={AUTH_COPY[lang].service_unavailable}
          retryLabel={t.retry}
          onRetry={() => setAttempt((n) => n + 1)}
        />
      )}
      {state.status === 'unauthenticated' && (
        <SignInScreen
          t={t} lang={lang} setLang={setLang} theme={theme} toggleTheme={toggleTheme}
          notice={state.expired ? { kind: 'expired' } : undefined}
        />
      )}
      {state.status === 'request' && (
        <RequestAccessScreen
          t={t} lang={lang} setLang={setLang} theme={theme} toggleTheme={toggleTheme}
          onExit={leaveRequest}
        />
      )}
      {state.status === 'noAccess' && (
        <NoAccessPage
          t={t} lang={lang} setLang={setLang} theme={theme} toggleTheme={toggleTheme}
          email={state.user.email}
          canCreate={state.user.canCreateCompanies}
          removed={state.removed}
          onCreate={() => {
            const current = stateRef.current
            if (current.status === 'noAccess') setBoundary({ status: 'wizard', user: current.user, memberships: current.memberships })
          }}
          onSignOut={signOutAll}
          invitations={invitations}
          onReconcile={reconcile}
          onReenter={() => setAttempt((n) => n + 1)}
        />
      )}
      {state.status === 'invite' && (
        <InviteLinkPage
          t={t} lang={lang} setLang={setLang} theme={theme} toggleTheme={toggleTheme}
          user={state.user}
          memberships={state.memberships}
          invitations={invitations}
          onReconcile={reconcile}
          onTerminal={inviteTerminal}
          onLeave={leaveInvite}
          onUseAnotherAccount={() => handleSignOut(true)}
        />
      )}
      {state.status === 'wizard' && (
        <FirstRun
          t={t} lang={lang} setLang={setLang} theme={theme} toggleTheme={toggleTheme}
          onSignOut={signOutAll}
          demoCompanyPending={state.user.demoCompanyPending === true}
          onDone={(companyId) => {
            createdRef.current = companyId
            void completeFirstRun()
          }}
          completion={completion}
          onRetryCompletion={() => { void completeFirstRun() }}
        />
      )}
      {state.status === 'authenticated' && (
        <App
          onSignOut={signOutAll}
          lang={lang}
          setLang={setLang}
          theme={theme}
          toggleTheme={toggleTheme}
          clients={state.clients}
          activeId={resolveClient(state.clients, storedClientId).id}
          onSelectClient={handleSelectClient}
          onRefreshClients={refreshClients}
          userId={state.user.id}
          user={state.user}
          notice={state.notice}
          onDismissNotice={dismissNotice}
          onReconcile={reconcile}
          invitations={invitations}
        />
      )}
      {state.status === 'share' && (
        <ShareLinkPage
          t={t} lang={lang} setLang={setLang} theme={theme} toggleTheme={toggleTheme}
          onOpened={enterAgain}
          onLeave={enterAgain}
        />
      )}
      {state.status === 'guestEnded' && (
        <GuestEndedPage
          t={t} lang={lang} setLang={setLang} theme={theme} toggleTheme={toggleTheme}
          onSignIn={() => setBoundary({ status: 'unauthenticated' })}
        />
      )}
      {state.status === 'guest' && (
        <App
          key={state.guest.companyId}
          onSignOut={signOutAll}
          lang={lang}
          setLang={setLang}
          theme={theme}
          toggleTheme={toggleTheme}
          clients={[guestClient(state.guest)]}
          activeId={state.guest.companyId}
          guest={{
            session: state.guest,
            unavailable: state.unavailable,
            reloadKey: state.reloadKey,
            onRecheck: recheckGuest,
            onLeave: leaveGuest,
          }}
        />
      )}
      {state.status === 'admin' && (
        <AdminScreen
          t={t} lang={lang} setLang={setLang} theme={theme} toggleTheme={toggleTheme}
          userId={state.user.id}
          users={state.users}
          onSignOut={signOutAll}
          onLeave={leaveAdmin}
        />
      )}
    </GrovusProvider>
  )
}
